import { requestWithAbort, type RequestResponse } from '@atlassian/twg-common';

// ============================================================================
// Types
// ============================================================================

type CacheStatus = 'READY' | 'STALE';

export type UserNoDataResponse = {
	status: 'NO_DATA';
};

export type Provider3pEventsByProvider = {
	providerName: string;
	events180d: number;
};

export type UserMicrositeHeroMetricsResponse = {
	pagesCreated180d: number | null;
	commentsCreated180d: number | null;
	workItemsCreated180d: number | null;
	videosCreated180d: number | null;
	designsCreated180d: number | null;
	prsCreated180d: number | null;
	relationshipsCreated180d: number | null;
	totalItemsCreated180d: number | null;
	provider3pEventsByProvider: Provider3pEventsByProvider[] | null;
	provider3pEventsTotal180d: number | null;
	provider3pDistinctCount180d: number | null;
	provider3pLikelyServiceAccount: boolean | null;
	pctRankInOrg: number | null;
	orgUserCountForRank: number | null;
	computedAt: string;
	cacheStatus: CacheStatus;
};

// Alias kept for backward compat
export type UserMetricsResponse = UserMicrositeHeroMetricsResponse;

// ============================================================================
// Type Guards
// ============================================================================

export const isUserNoDataResponse = (
	metrics: UserMicrositeHeroMetricsResponse | UserNoDataResponse | unknown,
): metrics is UserNoDataResponse =>
	metrics != null &&
	typeof metrics === 'object' &&
	(metrics as { status?: string }).status === 'NO_DATA';

// ============================================================================
// Request Utility
// ============================================================================

export const requestUserMetrics = (
	orgId: string,
): {
	controller: AbortController;
	execute: Promise<RequestResponse<UserMicrositeHeroMetricsResponse | UserNoDataResponse>>;
} => {
	const { controller, execute } = requestWithAbort<
		UserMicrositeHeroMetricsResponse | UserNoDataResponse
	>('GET', `/gateway/api/twg-api/v1/microsite/metrics/user?organisationId=${orgId}`, null, undefined, [
		200, 404,
	]);
	return { controller, execute };
};

// ============================================================================
// Metric Selection
// ============================================================================

export const selectUserMetrics = (
	metrics: UserMicrositeHeroMetricsResponse | UserNoDataResponse | null,
): UserMicrositeHeroMetricsResponse | null => {
	if (metrics == null || isUserNoDataResponse(metrics)) {
		return null;
	}
	return metrics;
};

// ============================================================================
// Tier 1 Gating
// ============================================================================

const ACCOUNT_MATURITY_DAYS = 30;

const USER_METRIC_COVERAGE_FLOORS: ReadonlyArray<{
	field: keyof UserMicrositeHeroMetricsResponse;
	floor: number;
}> = [
	{ field: 'pagesCreated180d', floor: 10 },
	{ field: 'commentsCreated180d', floor: 30 },
	{ field: 'workItemsCreated180d', floor: 20 },
	{ field: 'videosCreated180d', floor: 23 },
	{ field: 'designsCreated180d', floor: 5 },
	{ field: 'prsCreated180d', floor: 16 },
	{ field: 'relationshipsCreated180d', floor: 1 },
	{ field: 'totalItemsCreated180d', floor: 1 },
	{ field: 'provider3pEventsTotal180d', floor: 1 },
	{ field: 'provider3pDistinctCount180d', floor: 1 },
	{ field: 'pctRankInOrg', floor: 0 },
	{ field: 'orgUserCountForRank', floor: 1 },
];

const MIN_COVERAGE = 8;

/**
 * Evaluate whether a user qualifies for Tier 1 (user-level metrics view).
 *
 * Gate 1: Account must be >= 30 days old.
 * Gate 2: >= 8 user metrics individually pass their per-metric floors.
 *
 * Returns false if:
 * - metrics is null or NO_DATA
 * - accountCreatedAt is empty or unparseable (fail safe: cascade to Tier 2)
 * - account is < 30 days old
 * - fewer than 8 metrics pass their floors
 */
export const evaluateUserTier = (
	metrics: UserMicrositeHeroMetricsResponse | UserNoDataResponse | null,
	accountCreatedAt: string,
): boolean => {
	const userMetrics = selectUserMetrics(metrics);
	if (!userMetrics) {
		return false;
	}

	// Gate 1: account maturity — fail safe if date is missing or unparseable
	if (!accountCreatedAt) {
		return false;
	}
	const createdAtMs = Date.parse(accountCreatedAt);
	if (!Number.isFinite(createdAtMs)) {
		return false;
	}
	const ageDays = (Date.now() - createdAtMs) / (1000 * 60 * 60 * 24);
	if (ageDays < ACCOUNT_MATURITY_DAYS) {
		return false;
	}

	// Gate 2: metric coverage
	const passingMetricCount = USER_METRIC_COVERAGE_FLOORS.filter(({ field, floor }) => {
		const value = userMetrics[field];
		return typeof value === 'number' && value >= floor;
	}).length;

	return passingMetricCount >= MIN_COVERAGE;
};
